import type { OrgChange } from '@live-stream/shared'
import type { HttpClient } from '../http.js'
import { SocketManager, type AppSocket } from '../socket.js'

export type OrgListener = (change: OrgChange) => void

/**
 * An organisation's pages, kept current by the server telling them, not by asking over and over.
 *
 * It holds one connection of its own (separate from a meeting's, so neither disturbs the other),
 * opened when the first page starts watching and closed when the last stops. The server sends a
 * small nudge (`OrgChange`: what kind of thing changed, and which team) when something in an
 * organisation changes, and the page asks for what it needs over the ordinary requests. A nudge
 * sent while the connection was down is lost, so after a reconnect every watcher is told to look
 * again.
 */
export class OrgEvents {
  private readonly manager: SocketManager
  private readonly listeners = new Map<string, Set<OrgListener>>()
  private socket: AppSocket | null = null
  private everConnected = false

  constructor(private readonly http: HttpClient) {
    // Anonymous to the resume machinery and remembering nothing: it is not a seat in a meeting.
    this.manager = new SocketManager({ baseUrl: http.baseUrl, storage: null })
  }

  /** Hear about an organisation's changes until the returned function is called. */
  watch(orgId: string, listener: OrgListener): () => void {
    let set = this.listeners.get(orgId)
    if (!set) {
      set = new Set()
      this.listeners.set(orgId, set)
    }
    set.add(listener)
    const sock = this.ensure()
    // Not yet connected: the connect handler follows every organisation in `listeners`.
    if (sock.connected) this.follow(sock, orgId)

    let stopped = false
    return () => {
      if (stopped) return
      stopped = true
      const mine = this.listeners.get(orgId)
      mine?.delete(listener)
      if (mine && mine.size === 0) {
        this.listeners.delete(orgId)
        if (this.socket?.connected) this.socket.emit('org:unwatch', { orgId }, () => undefined)
      }
      if (this.listeners.size === 0) this.close()
    }
  }

  private ensure(): AppSocket {
    if (this.socket) return this.socket
    const sock = this.manager.connect(() => this.http.freshAccessToken())
    sock.on('org:changed', (change) => {
      for (const listener of this.listeners.get(change.orgId) ?? []) listener(change)
    })
    sock.on('connect', () => {
      for (const orgId of this.listeners.keys()) this.follow(sock, orgId)
      // Whatever was sent while the connection was down is gone: look again.
      if (this.everConnected) {
        for (const [orgId, set] of this.listeners) {
          for (const listener of set) listener({ orgId, scope: 'org' })
        }
      }
      this.everConnected = true
    })
    this.socket = sock
    return sock
  }

  private follow(sock: AppSocket, orgId: string): void {
    sock.emit('org:watch', { orgId }, () => undefined)
  }

  private close(): void {
    this.manager.disconnect()
    this.socket = null
    this.everConnected = false
  }
}
